import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Button, Card, CardContent } from '@/components/ui'
import { useAcceptClubInvite, useClubInvitePreview } from '@/hooks/clubs'

export function JoinClubPage() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { data: invite, isLoading } = useClubInvitePreview(token)
  const accept = useAcceptClubInvite()

  const join = async () => {
    if (!token) return
    const clubId = await accept.mutateAsync(token)
    navigate(`/dashboard/clubs/${clubId}`, { replace: true })
  }

  return (
    <div className="mx-auto max-w-md py-8">
      <Card className="border-0 shadow-sm">
        <CardContent className="space-y-4 p-8 text-center">
          {isLoading ? (
            <div className="h-32 animate-pulse rounded bg-muted" />
          ) : !invite || !invite.is_valid ? (
            <>
              <div className="text-4xl">🔗</div>
              <p className="font-medium text-foreground">{t('clubs.join.invalid')}</p>
              <p className="text-sm text-muted-foreground">{t('clubs.join.invalidHint')}</p>
              <Link to="/dashboard/clubs" className="text-sm text-primary hover:underline">
                {t('clubs.backToClubs')}
              </Link>
            </>
          ) : (
            <>
              <div className="text-4xl">📚</div>
              <p className="text-sm text-muted-foreground">
                {t('clubs.join.invitedBy', { name: invite.invited_by ?? '' })}
              </p>
              <h1 className="break-words text-2xl font-bold text-foreground">{invite.club_name}</h1>
              {invite.club_description && (
                <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
                  {invite.club_description}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                👥 {t('clubs.memberCount', { count: Number(invite.member_count) })}
              </p>
              {invite.already_member ? (
                <Button onClick={() => navigate(`/dashboard/clubs/${invite.club_id}`)}>
                  {t('clubs.join.open')}
                </Button>
              ) : (
                <Button onClick={join} disabled={accept.isPending} className="w-full">
                  {accept.isPending ? t('common.loading') : t('clubs.join.accept')}
                </Button>
              )}
              {accept.isError && <p className="text-sm text-error">{t('errors.general')}</p>}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
